import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import A11yDialog, { DialogErrorLine } from '../../components/ui/A11yDialog';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  max-width: 720px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 30px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 22px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

const Row = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const CompetencyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #FAFBFC;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #E8ECF0;
`;

const CompetencyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 110px;
  gap: 12px;
  align-items: center;
`;

const CompetencyLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 4px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-style: italic;
`;

const RECOMMENDATION_KEYS = ['strong_yes', 'yes', 'neutral', 'no', 'strong_no'];

const safeParseArray = (s) => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
};

export default function ScorecardFillDialog({ open, interview, onClose, onSaved }) {
  const { t } = useTranslation();

  const [templates, setTemplates] = useState([]);
  const [scorecardId, setScorecardId] = useState('');
  const [competencies, setCompetencies] = useState([]);
  const [scores, setScores] = useState({}); // { [key]: '1'..'5' }
  const [notes, setNotes] = useState({}); // { [key]: string }
  const [recommendation, setRecommendation] = useState('neutral');
  const [freeText, setFreeText] = useState('');
  const [existingResponseId, setExistingResponseId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // FE-16 — mülakatçı değerlendirirken adayın CV'sini görebilmeli. Adres
  // DOM'a ASLA yazılmaz (özel depolama sınıfı): DocumentViewerModal onu
  // /v1/storage/view üzerinden çözer. Aday kaydını panelist de okuyabilir
  // (AtsCandidateDataAccess.GetById katılımcıyı kapsamda sayar).
  const [resumeFileUrl, setResumeFileUrl] = useState(null);
  const [viewingResume, setViewingResume] = useState(false);
  // FE-25 — "kirli form" kapısı. Puan/not/öneri/serbest metin girildiyse ESC
  // doğrudan kapatmaz, atma onayı sorar: mülakatçının girdiği TÜM puanlar
  // eskiden perdeye yanlışlıkla dokununca yok oluyordu.
  const [touched, setTouched] = useState(false);
  // FE-40 — istemci doğrulaması artık toast değil, diyalog içinde role="alert".
  const [formError, setFormError] = useState(null);

  // Reset state every time dialog opens for a new interview.
  useEffect(() => {
    if (!open) return;
    setScorecardId('');
    setCompetencies([]);
    setScores({});
    setNotes({});
    setRecommendation('neutral');
    setFreeText('');
    setExistingResponseId(null);
    setResumeFileUrl(null);
    setViewingResume(false);
    setTouched(false);
    setFormError(null);
  }, [open, interview?.id]);

  // Aday CV'si — yalnız diyalog açıkken ve aday kimliği geldiğinde çekilir.
  useEffect(() => {
    if (!open || !interview?.candidateId) return undefined;
    let cancelled = false;
    api.get(`/v1/ats-candidates/${interview.candidateId}`)
      .then((res) => {
        if (cancelled) return;
        // api.js GET'lerde reject ETMEZ — gerçek sinyal res.data.status.
        if (res.data?.status) setResumeFileUrl(res.data.data?.resumeFileUrl || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, interview?.candidateId]);

  // Load all active scorecard templates for the tenant.
  useEffect(() => {
    if (!open) return;
    setLoadingTemplates(true);
    api.get('/v1/ats-scorecards?pageSize=200&isActive[eq]=true')
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setTemplates(items.map((s) => ({ id: s.id, name: s.name, competenciesJson: s.competenciesJson })));
      })
      .catch(() => {})
      .finally(() => setLoadingTemplates(false));
  }, [open]);

  // Yalnız ÇAĞIRANIN kendi cevabı ön-yüklenir (kimlik ailesi sunucuda çözülür;
  // BE (interview, scorecard, evaluator) üzerinden upsert eder). Eski
  // `responses?interviewId[eq]=` listesi HERHANGİ bir değerlendiricinin en yeni
  // cevabını "düzenleme modu" diye forma dolduruyordu — ikinci panelist
  // birincinin puanlarını gönderiyordu. `data` null ise form boş açılır (doldur).
  useEffect(() => {
    if (!open || !interview?.id) return undefined;
    let cancelled = false;
    setLoadingExisting(true);
    api.get(`/v1/ats-scorecards/responses/mine?interviewId=${encodeURIComponent(interview.id)}`)
      .then((res) => {
        if (cancelled) return;
        const mine = res.data?.status ? res.data.data : null;
        if (!mine) return;
        setExistingResponseId(mine.id);
        setScorecardId(mine.scorecardId);
        setRecommendation(mine.overallRecommendation || 'neutral');
        setFreeText(mine.freeTextNotes || '');
        const answers = safeParseArray(mine.answersJson);
        const sc = {};
        const nt = {};
        answers.forEach((a) => {
          if (a && a.key != null) {
            if (a.score != null) sc[a.key] = String(a.score);
            if (a.note != null) nt[a.key] = String(a.note);
          }
        });
        setScores(sc);
        setNotes(nt);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [open, interview?.id]);

  // EO-23 — mülakata ŞABLON ÇAPALANMIŞSA form o şablonu zorlar: sunucu başka
  // bir ScorecardId'yi 400 ile reddediyor (ats.scorecard.notAnchoredToInterview),
  // dolayısıyla listeden serbest seçim kullanıcıyı hataya sürüklerdi.
  // Kendi cevabının ön-yüklemesinden SONRA koşar (çapa her zaman kazanır).
  useEffect(() => {
    if (!open || !interview?.scorecardId) return;
    setScorecardId(interview.scorecardId);
  }, [open, interview?.scorecardId, loadingExisting]);

  // When a template is picked, parse competencies for that template.
  useEffect(() => {
    if (!scorecardId) { setCompetencies([]); return; }
    const tpl = templates.find((x) => x.id === scorecardId);
    if (!tpl) { setCompetencies([]); return; }
    setCompetencies(safeParseArray(tpl.competenciesJson));
  }, [scorecardId, templates]);

  const templateOptions = useMemo(() => templates.map((tpl) => ({ id: tpl.id, name: tpl.name })), [templates]);
  const recommendationOptions = useMemo(() => RECOMMENDATION_KEYS.map((r) => ({
    id: r, name: t(`ats.scorecard.recommendation.${r}`, { defaultValue: r }),
  })), [t]);

  const handleSubmit = async () => {
    setFormError(null);
    if (!interview?.id || !scorecardId) {
      setFormError(t('ats.scorecard.fill.requiredFields'));
      return;
    }
    const answers = competencies.map((c) => ({
      key: c.key,
      score: scores[c.key] != null && scores[c.key] !== '' ? Number(scores[c.key]) : null,
      note: notes[c.key] || '',
    }));

    // Şablondaki HER yetkinliğe puan zorunlu — eksik set gönderilmez. Sunucu da
    // aynı kuralı uygular (ats.scorecard.answersIncomplete); yetkinliksiz şablon
    // zaten gönderilemez (buton pasif).
    if (competencies.length === 0 || answers.some((a) => a.score == null || !Number.isFinite(a.score))) {
      setFormError(t('ats.scorecard.fill.allScoresRequired'));
      return;
    }

    // Bound each score to its competency's [scaleMin, scaleMax] (defaults 1-5).
    // The number input's min/max are advisory only — a typed value isn't clamped,
    // so guard here too. Backend enforces the same rule authoritatively.
    const outOfRange = answers.some((a) => {
      const c = competencies.find((x) => x.key === a.key);
      const min = Number(c?.scaleMin ?? 1);
      const max = Number(c?.scaleMax ?? 5);
      return a.score < min || a.score > max;
    });
    if (outOfRange) {
      setFormError(t('responseErrors.ats.scorecard.scoreOutOfRange'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/v1/ats-scorecards/responses', {
        interviewId: interview.id,
        scorecardId,
        overallRecommendation: recommendation || 'neutral',
        answersJson: JSON.stringify(answers),
        freeTextNotes: freeText || null,
      });
      if (res.data?.status) {
        onSaved?.(res.data.data);
        onClose?.();
      }
    } catch {
      // interceptor toasts
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <A11yDialog
      open={open}
      onClose={onClose}
      dirty={touched}
      overlay={Overlay}
      card={Card}
      overlayProps={{ $open: true }}
    >
      {({ titleId }) => (
      <>
        <Header>
          <div>
            <Title id={titleId}>
              {existingResponseId
                ? t('ats.interview.editScorecard')
                : t('ats.interview.fillScorecard')}
            </Title>
            <Subtitle>
              {interview?.candidateFullName || ''}
              {interview?.jobPostingTitle ? ` — ${interview.jobPostingTitle}` : ''}
            </Subtitle>
          </div>
          {resumeFileUrl && (
            <Tooltip text={t('ats.interview.viewResume')}>
              <IconButton
                data-testid="scorecard-resume-trigger"
                aria-label={t('ats.interview.viewResume')}
                onClick={() => setViewingResume(true)}
              >
                <FileText size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Header>

        <Row>
          <GenericSelect
            label={t('ats.scorecard.template')}
            options={templateOptions}
            value={scorecardId}
            onChange={(id) => { setTouched(true); setFormError(null); setScorecardId(id || ''); }}
            placeholder={loadingTemplates ? t('common.loading') : t('ats.scorecard.selectTemplate')}
            required
            disabled={loadingTemplates || loadingExisting || !!interview?.scorecardId}
          />
          {interview?.scorecardId && <Hint>{t('ats.interview.scorecardAnchored')}</Hint>}
        </Row>

        {!loadingTemplates && templates.length === 0 && (
          <Empty>{t('ats.scorecard.fill.noTemplates')}</Empty>
        )}

        {competencies.length > 0 && (
          <Row>
            <CompetencyLabel style={{ marginBottom: 8 }}>
              {t('ats.scorecard.competencies')}
            </CompetencyLabel>
            <CompetencyList>
              {competencies.map((c) => (
                <div key={c.key}>
                  <CompetencyRow>
                    <CompetencyLabel>{c.label || c.key}</CompetencyLabel>
                    <TextInput
                      type="number"
                      min={c.scaleMin ?? 1}
                      max={c.scaleMax ?? 5}
                      step="1"
                      value={scores[c.key] || ''}
                      onChange={(e) => { setTouched(true); setFormError(null); setScores({ ...scores, [c.key]: e.target.value }); }}
                      placeholder={`${c.scaleMin ?? 1}-${c.scaleMax ?? 5}`}
                    />
                  </CompetencyRow>
                  {c.descriptor && <Hint>{c.descriptor}</Hint>}
                  <TextInput
                    type="textarea"
                    rows={2}
                    value={notes[c.key] || ''}
                    onChange={(e) => { setTouched(true); setNotes({ ...notes, [c.key]: e.target.value }); }}
                    placeholder={t('ats.scorecard.fill.notePlaceholder')}
                  />
                </div>
              ))}
            </CompetencyList>
          </Row>
        )}

        {scorecardId && competencies.length === 0 && (
          <Empty>{t('ats.scorecard.fill.noCompetencies')}</Empty>
        )}

        <Row>
          <GenericSelect
            label={t('ats.scorecard.overallRecommendation')}
            options={recommendationOptions}
            value={recommendation}
            onChange={(id) => { setTouched(true); setRecommendation(id || 'neutral'); }}
            required
          />
        </Row>

        <Row>
          <TextInput
            label={t('ats.scorecard.freeTextNotes')}
            type="textarea"
            rows={3}
            value={freeText}
            onChange={(e) => { setTouched(true); setFreeText(e.target.value); }}
          />
        </Row>

        {formError && <DialogErrorLine role="alert" data-testid="scorecard-form-error">{formError}</DialogErrorLine>}

        <Actions>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting || !scorecardId || competencies.length === 0}
          >
            {existingResponseId ? t('common.save') : t('ats.scorecard.fill.submit')}
          </Button>
        </Actions>
      </>
      )}
    </A11yDialog>
    {/* CV önizleyicisi skorkart diyaloğunun KARDEŞİ olarak çizilir, İÇİNDE değil:
        kendi perdesi (z-index 2000) zaten skorkartın üstünde durur ve iki
        diyalogun odak tuzağı iç içe geçmez. ESC'in hangisini kapatacağını
        `dialogA11y`nin açık-diyalog yığını belirler — üstteki önizleyicidir. */}
    <DocumentViewerModal
      open={viewingResume}
      fileUrl={resumeFileUrl}
      title={t('ats.application.resume.title')}
      onClose={() => setViewingResume(false)}
    />
    </>
  );
}
